import { CkEthMinterCanister } from '@icp-sdk/canisters/cketh'
import { HttpAgent } from '@icp-sdk/core/agent'
import { Principal } from '@icp-sdk/core/principal'
import { encodeFunctionData, erc20Abi, type PublicClient } from 'viem'
import minterAbi from '../abi/ckethminter.abi.json'
import { config } from '../appConfig'
import OrdinalsClient from '../clients/OrdinalsClient'
import { solanaClient } from '../clients/SolanaClient'
import TatumClient from '../clients/TatumClient'
import { ETH_CHAIN_ID } from '../constants/business'
import { CK_MINTER_CANISTER_ID, SEPOLIA_CK_MINTER_CANISTER_ID } from '../constants/canisterIds'
import { SOL_ID } from '../constants/tokenIds'
import { BTCNFT, Chain, EVMChain, EvmToken, PlugToken } from '../interfaces/common'
import { UTXO, UTXOChain } from '../interfaces/walletActions'
import { addMultiplier } from './bigint'
import { asSolanaToken } from './castToken'
import { getEthProvider } from './getEthProvider'
import principalToBytes32 from './principalToBytes32'
import { getAssociatedTokenAccountCreationFee, SOLANA_BASE_FEE } from './solana'

const A_LOT_OF_ETH = 10n ** 25n

export class GasCalculator {
  agent: HttpAgent
  chain?: EVMChain
  ethProvider?: PublicClient
  minterInfo?: {
    ethMinterAddress?: string
    erc20MinterAddress?: string
  }

  constructor(chain?: EVMChain) {
    this.agent = config.getAgent()

    if (!chain) return

    this.chain = chain
    this.ethProvider = getEthProvider(chain.rpcEndpoint)
  }

  async estimateErc20TransferGas(
    fromAddress: string,
    tokenAddress: string,
    transferAmount: number
  ) {
    const { erc20MinterAddress } = await this.getMinterInfo()

    if (!erc20MinterAddress || !this.ethProvider) return

    const data = encodeFunctionData({
      abi: erc20Abi,
      functionName: 'transfer',
      args: [erc20MinterAddress as `0x${string}`, BigInt(transferAmount)],
    })

    const gas = await this.ethProvider.estimateGas({
      account: fromAddress as `0x${string}`,
      to: tokenAddress as `0x${string}`,
      // Override state with fake balance to prevent error if not enough ETH for gas fee
      // if user is estimating gas for amount larger than their balance
      stateOverride: [
        {
          address: tokenAddress as `0x${string}`,
          balance: A_LOT_OF_ETH,
        },
      ],
      data,
    })

    const multiplier = this.chain?.chainId === ETH_CHAIN_ID ? 2n : 3n

    return gas * multiplier
  }

  async estimateErc20ApproveGas(
    fromAddress: string,
    tokenAddress: string,
    transferAmount: number,
    contractAddress?: string
  ) {
    const { erc20MinterAddress } = await this.getMinterInfo()

    if (!erc20MinterAddress || !this.ethProvider) return

    const data = encodeFunctionData({
      abi: erc20Abi,
      functionName: 'approve',
      args: [(contractAddress || erc20MinterAddress) as `0x${string}`, BigInt(transferAmount)],
    })

    return this.ethProvider.estimateGas({
      account: fromAddress as `0x${string}`,
      to: tokenAddress as `0x${string}`,
      data,
    })
  }

  async estimateEthDepositGas(principalId: string) {
    const { ethMinterAddress } = await this.getMinterInfo()

    if (!ethMinterAddress || !this.ethProvider) return

    const data = encodeFunctionData({
      abi: minterAbi,
      functionName: 'deposit',
      args: [principalToBytes32(principalId)],
    })

    const estimate = await this.ethProvider.estimateGas({
      to: ethMinterAddress as `0x${string}`,
      data,
    })

    return BigInt(Math.ceil(Number(estimate) * 1.5))
  }

  async getEthGas(from: string | undefined, transferAmount: bigint | undefined) {
    if (!this.ethProvider) return

    const gas = await this.ethProvider.estimateFeesPerGas()

    let failed = false
    let gasLimit: bigint | undefined

    if (from && transferAmount) {
      try {
        gasLimit = await this.ethProvider.estimateGas({
          account: from as `0x${string}`,
          value: transferAmount,
          // Override state with fake balance to prevent error
          // if user is estimating gas for amount larger than their balance
          stateOverride: [
            {
              address: from as `0x${string}`,
              balance: A_LOT_OF_ETH,
            },
          ],
        })
      } catch (e) {
        failed = true
        gasLimit = undefined
      }
    }

    return {
      failed,
      gasLimit: gasLimit && addMultiplier(gasLimit, 1.5),
      maxFeePerGas: gas.maxFeePerGas,
      maxPriorityFeePerGas: gas.maxPriorityFeePerGas,
    }
  }

  static utxosForAmount(
    utxos: UTXO[],
    userOrdinals: BTCNFT[],
    requiredSatoshis: bigint,
    selectedOrdinal?: BTCNFT
  ): UTXO[] {
    let utxosToUse: UTXO[] = []

    const ordinalUtxo = selectedOrdinal
      ? utxos.find(
          (utxo) =>
            utxo.mintTxid === selectedOrdinal.txId && utxo.mintIndex === selectedOrdinal.mintIndex
        )
      : undefined

    const { spendable } = TatumClient.splitOrdinalUtxos(utxos, userOrdinals, Chain.BTC)
    const sortedUtxos = spendable.sort((a, b) => b.value - a.value)

    for (const utxo of sortedUtxos) {
      if (utxosToUse.reduce((acc, u) => acc + u.value, 0) >= requiredSatoshis) {
        break
      }
      utxosToUse.push(utxo)
    }

    // add ordinal only if sending ordinal
    if (ordinalUtxo) {
      utxosToUse.push(ordinalUtxo)
    }

    return utxosToUse
  }

  async estimateUTXOTransferFee(fromAddress: string, amount: bigint, chain: UTXOChain = Chain.BTC) {
    const baseFee = await TatumClient.getChainFee(chain)
    const utxos = await TatumClient.getUTXOs(fromAddress, chain)

    // Ordinals only apply to Bitcoin
    let ordinals: BTCNFT[] = []
    if (chain === Chain.BTC) {
      const ordinalClient = new OrdinalsClient()
      ordinals = await ordinalClient.getOrdinals(fromAddress)
    }

    return TatumClient.estimateFee(utxos, ordinals, fromAddress, amount, baseFee, chain)
  }

  async estimateMaxUTXOSendable(fromAddress: string, chain: UTXOChain = Chain.BTC) {
    const baseFee = await TatumClient.getChainFee(chain)
    const utxos = await TatumClient.getUTXOs(fromAddress, chain)

    let ordinals: BTCNFT[] = []
    if (chain === Chain.BTC) {
      const ordinalClient = new OrdinalsClient()
      ordinals = await ordinalClient.getOrdinals(fromAddress)
    }

    return TatumClient.estimateMaxSendable(utxos, ordinals, baseFee, chain)
  }

  async estimateSolTransferFee(token: PlugToken | undefined, toAddress: string) {
    let feeAmount = SOLANA_BASE_FEE

    if (token?.chain === Chain.SOL && token.id !== SOL_ID) {
      const splToken = asSolanaToken(token)
      const hasATA = await solanaClient.hasAssociatedTokenAccount(toAddress, splToken)

      if (!hasATA) {
        feeAmount += getAssociatedTokenAccountCreationFee(splToken)
      }
    }

    return feeAmount
  }

  async estimateEthTransferFee(token: EvmToken, fromAddress: string, amount: bigint) {
    const gas = await this.getEthGas(fromAddress, amount)

    let gasLimit = gas?.gasLimit

    if (token.standard === 'ERC20') {
      gasLimit = await this.estimateErc20TransferGas(fromAddress, token.address!, Number(amount))
    }

    if (typeof gasLimit === 'bigint' && gas) {
      return gasLimit * (gas.maxFeePerGas + gas.maxPriorityFeePerGas)
    }
  }

  private async getMinterInfo() {
    if (!this.minterInfo) {
      const isMainnet = config.fetchEnv('ETH_CHAIN') !== 'sepolia'
      const cannisterId = isMainnet ? CK_MINTER_CANISTER_ID : SEPOLIA_CK_MINTER_CANISTER_ID

      const minterCanister = CkEthMinterCanister.create({
        canisterId: Principal.from(cannisterId),
        agent: this.agent,
      })

      const minterInfo = await minterCanister.getMinterInfo({ certified: true })

      this.minterInfo = {
        ethMinterAddress: minterInfo.eth_helper_contract_address[0],
        erc20MinterAddress: minterInfo.erc20_helper_contract_address[0],
      }
    }

    return this.minterInfo!
  }
}
